/**
 * The site's router (ADR-041): the History API and a route table, nothing more. It does what the
 * React site used from React Router: the current location, params of the matched route, the query
 * string as state (`searchParams`/`setSearchParams`), `navigate` with replace and state, and plain
 * <a href> links that navigate in place (see `interceptLinks`).
 */

export interface NavigateOptions {
  replace?: boolean;
  state?: unknown;
}

class Location {
  pathname = $state(window.location.pathname);
  search = $state(window.location.search);
  hash = $state(window.location.hash);
  state = $state<unknown>((window.history.state as { usr?: unknown } | null)?.usr ?? null);

  sync(): void {
    this.pathname = window.location.pathname;
    this.search = window.location.search;
    this.hash = window.location.hash;
    this.state = (window.history.state as { usr?: unknown } | null)?.usr ?? null;
  }
}

export const location = new Location();

window.addEventListener('popstate', () => location.sync());

/** Goes to `to` (a path with an optional query string), like React Router's navigate. */
export function navigate(to: string, options: NavigateOptions = {}): void {
  const url = new URL(to, window.location.href);
  const target = url.pathname + url.search + url.hash;
  const current = window.location.pathname + window.location.search + window.location.hash;
  const entry = { usr: options.state ?? null };
  // React Router replaces instead of pushing when the target is the current location.
  if (options.replace || target === current) window.history.replaceState(entry, '', target);
  else window.history.pushState(entry, '', target);
  location.sync();
}

/** The query string as state; a change navigates (pushes) like React Router's setSearchParams. */
export function searchParams(): URLSearchParams {
  return new URLSearchParams(location.search);
}

export function setSearchParams(
  next: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams | Record<string, string>),
  options: NavigateOptions = {},
): void {
  const value = typeof next === 'function' ? next(searchParams()) : next;
  const params = value instanceof URLSearchParams ? value : new URLSearchParams(value);
  const query = params.toString();
  navigate(`${location.pathname}${query ? `?${query}` : ''}`, options);
}

/** Same-origin <a href> clicks navigate in place (React Router's <Link>); others behave natively. */
export function interceptLinks(root: Document = document): void {
  root.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element | null)?.closest?.('a');
    if (!a || !a.href || a.target || a.hasAttribute('download')) return;
    const url = new URL(a.href);
    if (url.origin !== window.location.origin) return;
    e.preventDefault();
    navigate(url.pathname + url.search + url.hash, { replace: a.dataset.replace === 'true' });
  });
}

export interface Match {
  params: Record<string, string>;
}

/** Matches `/o/:orgId/patients/:patientId` against a path; null when it does not match. */
export function matchPath(pattern: string, pathname: string): Match | null {
  const p = pattern.split('/').filter(Boolean);
  const s = pathname.split('/').filter(Boolean);
  if (p.length !== s.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i += 1) {
    const part = p[i] as string;
    const seg = s[i] as string;
    if (part.startsWith(':')) params[part.slice(1)] = decodeURIComponent(seg);
    else if (part !== seg) return null;
  }
  return { params };
}
